import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Category, Wallpaper } from '@/types'
import type { LibraryOverview } from '@/lib/wallpapers'

import Link from 'next/link'
import { ArrowRight, ImageOff } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { EmptyState } from '@/components/generals/empty-state'
import { SectionHeading } from '@/components/generals/section-heading'
import { Hero } from '@/components/landing/hero'
import { DeviceSplit } from '@/components/landing/device-split'
import { PhoneRow } from '@/components/wallpapers/phone-row'
import { ColorTiles } from '@/components/wallpapers/color-swatches'
import { CategoryCard } from '@/components/wallpapers/category-card'
import { WallpaperMasonry } from '@/components/wallpapers/wallpaper-masonry'
import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'

import {
    getCategories,
    getLatestWallpapers,
    getLibraryOverview,
    getPhoneWallpapers,
    getPopularWallpapers,
    getSpotlightWallpapers
} from '@/lib/wallpapers'

import { REVALIDATE_SECONDS } from '@/constants/site'

interface HomeProps {
    spotlight: Array<Wallpaper>
    latest: Array<Wallpaper>
    phones: Array<Wallpaper>
    popular: Array<Wallpaper>
    categories: Array<Category>
    overview: LibraryOverview
}

interface SeeAllLinkProps {
    href: string
    children: ReactNode
}

const SeeAllLink: FC<SeeAllLinkProps> = ({ href, children }): ReactNode => {
    return (
        <Link href={href} className={cn(buttonVariants({ variant: 'outline', size: 'lg' }), 'rounded-full')}>
            {children}
            <ArrowRight aria-hidden='true' />
        </Link>
    )
}

const Home: FC<HomeProps> = ({ spotlight, latest, phones, popular, categories, overview }): ReactNode => {
    const shownCategories = categories.filter(category => category.wallpaper_count > 0)

    return (
        <Layout overlayHeader>
            <Seo ogImage={spotlight[0]?.image} />

            <Hero
                wallpapers={spotlight}
                total={overview.total}
                categoryCount={shownCategories.length}
                tags={overview.tags}
            />

            {overview.total === 0 && (
                <Container className='py-20'>
                    <EmptyState
                        icon={<ImageOff aria-hidden='true' className='size-5' />}
                        title='New wallpapers are on the way'
                        description='The gallery is being filled right now. Check back soon for fresh desktop and phone wallpapers.'
                    />
                </Container>
            )}

            {shownCategories.length > 0 && (
                <section id='categories' aria-labelledby='categories-title' className='pt-20 sm:pt-24'>
                    <Container>
                        <SectionHeading
                            id='categories-title'
                            eyebrow='Categories'
                            title='Pick a mood'
                            description='From snowbound peaks to neon streets, every collection is hand-picked.'
                            action={<SeeAllLink href='/categories'>All categories</SeeAllLink>}
                        />

                        <ul className='mt-10 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 xl:grid-cols-4'>
                            {shownCategories.map(category => (
                                <li key={category.id}>
                                    <CategoryCard category={category} />
                                </li>
                            ))}
                        </ul>
                    </Container>
                </section>
            )}

            {latest.length > 0 && (
                <section id='latest' aria-labelledby='latest-title' className='pt-20 sm:pt-24'>
                    <Container>
                        <SectionHeading
                            id='latest-title'
                            eyebrow='Just added'
                            title='Fresh on the wall'
                            description='The newest wallpapers in the collection, for every screen.'
                            action={<SeeAllLink href='/wallpapers'>Browse all</SeeAllLink>}
                        />

                        <WallpaperMasonry wallpapers={latest} className='mt-10' />
                    </Container>
                </section>
            )}

            {phones.length > 0 && (
                <section id='phone' aria-labelledby='phone-title' className='pt-20 sm:pt-24'>
                    <Container>
                        <PhoneRow
                            wallpapers={phones}
                            label='Phone wallpapers'
                            heading={(
                                <SectionHeading
                                    id='phone-title'
                                    eyebrow='Phone'
                                    title={(
                                        <>
                                            Made for your
                                            {' '}
                                            <em>lock screen</em>
                                        </>
                                    )}
                                    description='Tall wallpapers that fill your phone from edge to edge.'
                                />
                            )}
                            action={<SeeAllLink href='/wallpapers?device=phone'>All phone wallpapers</SeeAllLink>}
                        />
                    </Container>
                </section>
            )}

            {overview.colors.length > 0 && (
                <section id='colors' aria-labelledby='colors-title' className='pt-20 sm:pt-24'>
                    <Container>
                        <SectionHeading
                            id='colors-title'
                            eyebrow='Color'
                            title='Browse by color'
                            description='Match your setup, your phone case or simply your mood.'
                        />

                        <div className='mt-10'>
                            <ColorTiles colors={overview.colors} />
                        </div>
                    </Container>
                </section>
            )}

            {popular.length > 0 && (
                <section id='popular' aria-labelledby='popular-title' className='pt-20 sm:pt-24'>
                    <Container>
                        <SectionHeading
                            id='popular-title'
                            eyebrow='Trending'
                            title='Most downloaded'
                            description='What everyone else is putting on their screens right now.'
                            action={<SeeAllLink href='/wallpapers?sort=popular'>See the full list</SeeAllLink>}
                        />

                        <WallpaperMasonry wallpapers={popular} className='mt-10' />
                    </Container>
                </section>
            )}

            {overview.total > 0 && (
                <section id='devices' aria-labelledby='devices-title' className='pt-20 sm:pt-24'>
                    <Container>
                        <SectionHeading
                            id='devices-title'
                            eyebrow='Every screen'
                            title='Desktop or phone?'
                            description='Jump straight to wallpapers shaped for the screen in front of you.'
                        />

                        <div className='mt-10'>
                            <DeviceSplit
                                desktopCount={overview.desktop}
                                phoneCount={overview.phone}
                                desktopWallpaper={spotlight[1] ?? spotlight[0]}
                                phoneWallpaper={phones[0]}
                            />
                        </div>
                    </Container>
                </section>
            )}
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    const [spotlight, latest, phones, popular, categories, overview] = await Promise.all([
        getSpotlightWallpapers(4),
        getLatestWallpapers(12),
        getPhoneWallpapers(10),
        getPopularWallpapers(8),
        getCategories(),
        getLibraryOverview(6)
    ])

    return {
        props: {
            spotlight,
            latest,
            phones,
            popular,
            categories,
            overview
        },
        revalidate: REVALIDATE_SECONDS
    }
}

export default Home
